/**
 * Jedyny katalog nazw wariantów po stronie FE (build-time).
 * Zasilane z REACT_APP_VARIANT / REACT_APP_NAME. Weryfikacja zgodności z BE:
 * GET /olympics/version → pole `variant`.
 */

export type OlympiadVariant = 'small' | 'big' | 'filo';

export type VariantCapabilities = {
	'branding.shortName': string;
	'branding.appName': string;
	'schools.rspoTypes': number[];
	'districts.mazowszeLabels': [string, string];
	'registration.specializationEnabled': boolean;
	'registration.studentPhoneRequired': boolean;
	'registration.underageOnly': boolean;
	'registration.maxTeachersForStudent': number;
	'registration.guardianRolesSplit': boolean;
	'registration.maxScientificGuardians': number;
	'registration.maxSchoolGuardians': number;
	'teacher.categoryLabels': {
		teacher: string;
		scientific_supervisor: string;
		school_scientific?: string;
	};
	'stage2b.requireMinPointsPerOralPart': boolean;
	'stage2b.showOralDescription': boolean;
	'certificates.templatePrefix': string;
	public_cms: boolean;
};

const PROFILES: Record<OlympiadVariant, VariantCapabilities> = {
	big: {
		'branding.shortName': 'OLiJP',
		'branding.appName': 'OLiJP',
		'schools.rspoTypes': [90, 14, 27, 24, 26, 86, 16],
		'districts.mazowszeLabels': ['Warszawa', 'Mazowsze'],
		'registration.specializationEnabled': true,
		'registration.studentPhoneRequired': false,
		'registration.underageOnly': false,
		'registration.maxTeachersForStudent': 3,
		'registration.guardianRolesSplit': false,
		'registration.maxScientificGuardians': 3,
		'registration.maxSchoolGuardians': 0,
		'teacher.categoryLabels': {
			teacher: 'Nauczyciel w szkole ponadpodstawowej',
			scientific_supervisor: 'Opiekun naukowy',
		},
		'stage2b.requireMinPointsPerOralPart': true,
		'stage2b.showOralDescription': false,
		'certificates.templatePrefix': '',
		public_cms: true,
	},
	small: {
		'branding.shortName': 'OLiJP-SP',
		'branding.appName': 'OLiJPSP',
		'schools.rspoTypes': [3],
		'districts.mazowszeLabels': ['mazowieckie I', 'mazowieckie II'],
		'registration.specializationEnabled': false,
		'registration.studentPhoneRequired': false,
		'registration.underageOnly': true,
		'registration.maxTeachersForStudent': 3,
		'registration.guardianRolesSplit': false,
		'registration.maxScientificGuardians': 3,
		'registration.maxSchoolGuardians': 0,
		'teacher.categoryLabels': {
			teacher: 'Nauczyciel w szkole podstawowej',
			scientific_supervisor: 'Opiekun naukowy',
		},
		'stage2b.requireMinPointsPerOralPart': false,
		'stage2b.showOralDescription': true,
		'certificates.templatePrefix': 'small-',
		public_cms: true,
	},
	filo: {
		'branding.shortName': 'OF',
		'branding.appName': 'OF',
		'schools.rspoTypes': [90, 14, 27, 24, 26, 86, 16],
		'districts.mazowszeLabels': ['Warszawa', 'Mazowsze'],
		'registration.specializationEnabled': false,
		'registration.studentPhoneRequired': true,
		'registration.underageOnly': false,
		'registration.maxTeachersForStudent': 3,
		'registration.guardianRolesSplit': true,
		'registration.maxScientificGuardians': 2,
		'registration.maxSchoolGuardians': 1,
		'teacher.categoryLabels': {
			teacher: 'Opiekun szkolny',
			scientific_supervisor: 'Opiekun naukowy',
			school_scientific: 'Opiekun szkolno-naukowy',
		},
		'stage2b.requireMinPointsPerOralPart': true,
		'stage2b.showOralDescription': false,
		'certificates.templatePrefix': '',
		public_cms: false,
	},
};

/** Nazwa wariantu z env buildu. */
export function getBuildVariant(): OlympiadVariant {
	const fromVariant = (process.env.REACT_APP_VARIANT || '').toLowerCase();
	if (fromVariant === 'filo' || fromVariant === 'small' || fromVariant === 'big') {
		return fromVariant;
	}
	const name = process.env.REACT_APP_NAME || '';
	if (name === 'OLiJPSP') {
		return 'small';
	}
	if (name === 'OF' || name === 'OlimpiadaFilozoficzna') {
		return 'filo';
	}
	return 'big';
}

export function isSmallOlympicVariant(): boolean {
	return getBuildVariant() === 'small';
}

export function isBigOlympicVariant(): boolean {
	return getBuildVariant() === 'big';
}

export function isFiloOlympicVariant(): boolean {
	return getBuildVariant() === 'filo';
}

export function getVariantCapability<K extends keyof VariantCapabilities>(key: K): VariantCapabilities[K] {
	return PROFILES[getBuildVariant()][key];
}

/** Hook-style accessor (build-time; bez fetch). */
export function useVariant(): {
	variant: OlympiadVariant;
	isSmall: boolean;
	isBig: boolean;
	isFilo: boolean;
	get: <K extends keyof VariantCapabilities>(key: K) => VariantCapabilities[K];
} {
	const variant = getBuildVariant();
	return {
		variant,
		isSmall: variant === 'small',
		isBig: variant === 'big',
		isFilo: variant === 'filo',
		get: getVariantCapability,
	};
}

export function getVariantProfile(): VariantCapabilities {
	return PROFILES[getBuildVariant()];
}
